import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'

import {
    Box,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    useDisclosure,
    DrawerCloseButton,
    Text,
    Link,
    Image,

    /* Hooks */
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Styles */
import { useMyRewardsStyles } from '../styles/myRewardsStyles'

/* Hooks */
import { useOptimizely } from '../../../contexts/optimizely-context'
import {preferenceToBool} from '../../../hooks/use-site-preferences'

/* Utils */
import { generateStyles } from '../../../components/optimizely/utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import NewsletterSubscribeDrawer from './newsletter-subscribe-drawer'

const EarningPoints = React.forwardRef(({ customer, loyaltyData, enablePointsForReview: enablePointsForReviewProp, onNewsletterSubscribed }, ref) => {
    const { rewardPageBlock } = useOptimizely()
    const styles = useMyRewardsStyles()
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const isGuest = !customer?.isRegistered
    const erbStyling = styles?.components?.exclusiveRewardsBlock
    const epStyling = styles?.components?.earningPoints
    const [cmsData, setCmsData] = useState(null)

    const breakpointTitle = useBreakpointValue({
        base: cmsData?.title?.mobileText,
        md: cmsData?.title?.tabletText,
        lg: cmsData?.title?.desktopText
    })

    // Refs and state for popup handling - must be called unconditionally (before any early return)
    const containerRef = useRef(null)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [popupContent, setPopupContent] = useState(null)
    const [popupType, setPopupType] = useState(null)
    const [popupData, setPopupData] = useState(null)

    const loyaltyMemberRecord =
        loyaltyData?.loyaltyMemberRecord ?? loyaltyData?.data?.loyaltyMemberRecord ?? null

    const isShopOnlineInStoreTileCompleted = (record) =>
        record != null &&
        record.Shop_Store__c === true &&
        record.Shop_Web_App__c === true

    // This will fallback CMS
    const hardCodedData = {
        title: {
            mobileText: 'Ways to <span class="highlighted">Earn</span>',
            tabletText: 'EARNING POINTS, THE GARAGE COLLECTIF WAY',
            desktopText: 'EARNING POINTS, THE GARAGE COLLECTIF WAY',
        },
        subTitle: "And just like that... thanks to your points and your tier status, you're already earning rewards. Don't miss out!",
        waysToEarn: [{
            'tile-content-id': 'start-shopping-tile',
            'popup-content-id': 'start-shopping-popup',
            'completed': false
        }, {
            'tile-content-id': 'newsletter-tile',
            'popup-content-id': 'newsletter-popup',
            'completed': customer?.c_email_garage_optedIn
        }, {
            'tile-content-id': 'shop-tile',
            'popup-content-id': 'shop-popup',
            'completed': isShopOnlineInStoreTileCompleted(loyaltyMemberRecord)
        }, {
            'tile-content-id': 'subscribe-tile',
            'popup-content-id': 'subscribe-popup',
            'completed': customer?.c_sms_garage_optedIn
        }, {
            'tile-content-id': 'leave-review-tile',
            'popup-content-id': 'leave-review-popup',
            'completed': false
        }],
        waysToEarnDataMapping: {
            'start-shopping-tile': {
                icon: getAssetUrl('static/img/my-rewards/start-shopping.svg'),
                title: 'Start Shopping',
                subtitle: '1 point per £1',
                completedText: 'Completed'
            },
            'start-shopping-popup': {
                icon: getAssetUrl('static/img/my-rewards/start-shopping.svg'),
                title: 'Make a Purchase',
                content: `
                    <p class="er-modal-subtitle">Receive 1 point for every £1 you spend online or in-store.</p>
                    Points will be granted:
                    <ul>
                        <li>Online at the time of shipment</li>
                        <li>Buy online, Pick-up in-store, at the time of in-store pick-up.</li>
                        <li>In-store, at the time of purchase.</li>
                        <li>Online orders made in-store, at the time of shipment.</li>
                    </ul>
                    <br /><br /> Only Collectif members with an active account can earn points. Please allow 48 hours for the points to be added to the account. Applicable to amount spent before shipping fees. Points are rounded to the nearest euro. Excludes purchases made on e-gift cards, gift cards, gift wrap and shopping bags. A maximum 15,000 points can be earned within 12 months of your joining date. In case of return or cancellation, neither the points deducted from your account nor the cash-off discount will be refunded.
                `,
                note: 'For more information, refer to our <a href="/terms-conditions.html">Terms and Conditions</a> and <a href="/privacy-policy.html">Privacy Policy</a>'
            },
            'newsletter-tile': {
                icon: getAssetUrl('static/img/my-rewards/newsletter.svg'),
                title: 'Subscribe to our Newsletter',
                subtitle: '10 points',
                completedText: 'Completed',
                completedCtaLabel: 'Manage preferences',
                completedCtaHref: '/account/email'
            },
            'newsletter-popup': {
                icon: getAssetUrl('static/img/my-rewards/newsletter.svg'),
                title: 'Subscribe to our Newsletter',
                content: 'Sign up now & earn 10 points!<br /><br />*Points will be granted immediately, but please allow for 48 hours before your points are registered to your Garage Collectif account. Not applicable to members who were already subscribed to the newsletter.',
                note: 'For more information, please refer to our <a href="/privacy-policy.html">Privacy Policy</a>.'
            },
            'shop-tile': {
                icon: getAssetUrl('static/img/my-rewards/shop.svg'),
                title: 'Shop Online & In-Store',
                subtitle: '20 points',
                completedText: 'Completed'
            },
            'shop-popup': {
                icon: getAssetUrl('static/img/my-rewards/shop.svg'),
                title: 'More Than One Way To Shop',
                content: '<strong>Earn 20 bonus points the first time you complete both 1 online and 1 in-store purchase at Garage</strong> <br /><br /> Points will automatically be granted. Please allow 48 hours for the points to be added to your account. Not applicable to customers who have made purchases before joining the Garage Collectif.',
                note: 'For more information, please refer to our <a href="/terms-conditions.html">Terms and Conditions</a> and <a href="/privacy-policy.html">Privacy Policy</a>'
            },
            'subscribe-tile': {
                icon: getAssetUrl('static/img/my-rewards/subscribe.svg'),
                title: 'Subscribe to SMS',
                subtitle: '10 points',
                completedText: 'Completed'
            },
            'subscribe-popup': {
                icon: getAssetUrl('static/img/my-rewards/subscribe.svg'),
                title: 'Subscribe to SMS',
                content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.',
                note: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.'
            },
            'leave-review-tile': {
                icon: getAssetUrl('static/img/my-rewards/leave-review.svg'),
                title: 'Leave a review',
                subtitle: 'Up to 20 points',
                completedText: 'Completed'
            },
            'leave-review-popup': {
                icon: getAssetUrl('static/img/my-rewards/leave-review.svg'),
                title: 'LEAVE REVIEW, GET POINTS ',
                content: `Leaving an honest and authentic review about items you've purchased will entitle you to receive 10 points for each review (with an opportunity for an additional 10 points if a photo is posted). Maximum of three (3) reviews per calendar month and one review per product purchased with all mandatory fields completed will be eligible for Collectif points. Any duplicate reviews will not be considered. Review must be submitted through the Website and not through the app. All reviews will be subject to our review and will be marked as Incentivized. Points will be awarded regardless of rating, however there may be a delay of up to approximately 1 week after review is approved and published. We reserve the right to reject or remove any review that does not meet our guidelines. Points have no cash value and are non-redeemable or transferable.`,
            }
        }
    }

    const cmsDataModel = (rewardPageBlockData) => {
        const waysToEarn = [];
        const waysToEarnDataMapping = {};

        rewardPageBlockData?.WaysToEarnTileList?.forEach(item => {
            const block = item.ContentLink.Expanded;
            const rewardTileSFID = block.RewardTileSFID;

            // Determine completed condition based on RewardTileSFID
            let completed;
            switch (rewardTileSFID) {
                case "start_shopping":
                    completed = false;
                    break;
                case "getapp":
                    // TODO FD REWARDS: Update logic with correct values that are coming from the BD side.
                    // From what I know at this point this shouldn't be configured
                    completed = false;
                    break;
                case "shop_online_store":
                    completed = isShopOnlineInStoreTileCompleted(loyaltyMemberRecord);
                    break;
                case "get_notifications":
                    // TODO FD REWARDS: Update logic with correct values that are coming from the BD side.
                    // From what I know at this point this shouldn't be configured
                    completed = false;
                    break;
                    // TODO FD REWARDS: There is a typo here but it is intended since this is how the value is configured on CMS
                    // To be corrected after the CMS side will be corrected
                case "subsvribe_sms":
                    completed = customer?.c_sms_garage_optedIn;
                    break;
                case "":
                    // TODO FD REWARDS: This is the newsletter tile which don't have an rewardTileSFID on CMS
                    // It must be update with value after will be update on CMS
                    completed = customer?.c_email_garage_optedIn;
                    break;
                case "leave_review":
                    completed = false;
                    break;
                default:
                    completed = false;
            }

            // Add to waysToEarn array
            waysToEarn.push({
                'tile-content-id': `${rewardTileSFID}-tile`,
                'popup-content-id': `${rewardTileSFID}-popup`,
                'completed': completed
            });

            // Create tile mapping
            const tileKey = `${rewardTileSFID}-tile`;
            waysToEarnDataMapping[tileKey] = {
                icon: block.IconMedia?.Expanded?.Url || '',
                title: block.Title || '',
                subtitle: block.Description || '',
                completedText: 'Completed',
                titleStyle: block.RewardTileBlockTitleStyle,
                descriptionStyle: block.RewardTileBlockDescriptionStyle
            };

            // Create popup mapping
            if (block.Modal?.Expanded) {
                const modal = block.Modal.Expanded;
                const popupKey = `${rewardTileSFID}-popup`;

                waysToEarnDataMapping[popupKey] = {
                    icon: modal.IconMedia?.Expanded?.Url || '',
                    title: modal.Title || '',
                    content: modal.Description || '',
                    note: modal.AdditionalInfo || '',
                    titleStyle: modal.TileModalBlockTitleStyle,
                    descriptionStyle: modal.TileModalBlockDescriptionStyle,
                    additionalInfoStyle: modal.AdditionalInfoStyle
                };
            }
        });

        return {
            title: {
                mobileText: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.MobileText,
                tabletText: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.TabletText,
                desktopText: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.DesktopText,
                ResponsiveTextBlockTextStyle: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.ResponsiveTextBlockTextStyle
            },
            subTitle: "And just like that... thanks to your points and your tier status, you're already earning rewards. Don't miss out!",
            waysToEarn,
            waysToEarnDataMapping
        };
    }

    useEffect(() => {
        if (rewardPageBlock) {
            const model = cmsDataModel(rewardPageBlock)
            setCmsData(model?.waysToEarn?.length ? model : hardCodedData)
        } else {
            setCmsData(hardCodedData)
        }
    }, [rewardPageBlock])

    // From loyalty API when available; when sflmEnabled is No sync is not called so parent passes value from preferences API
    const enablePointsForReview = enablePointsForReviewProp !== undefined && enablePointsForReviewProp !== null
        ? !!enablePointsForReviewProp
        : (loyaltyData ? preferenceToBool(loyaltyData.enablePointsForReview) : false)

    const reviewReward = hardCodedData?.waysToEarn?.find(
        (reward) => reward?.['tile-content-id'] === 'leave-review-tile'
    )
    const reviewTileData = hardCodedData?.waysToEarnDataMapping?.['leave-review-tile']
    const reviewPopupData = hardCodedData?.waysToEarnDataMapping?.['leave-review-popup']

    let rewards = cmsData?.waysToEarn || []
    let rewardsDataMapping = cmsData?.waysToEarnDataMapping || {}

    // Leave a review tile: completed when Maximum_Reviews_per_Period__c is set on LoyaltyMemberRecord[0].
    const isLeaveReviewTileCompleted = (record) => {
        if (!record || typeof record !== 'object') return false
        const maxPerPeriod =
            record.Maximum_Reviews_per_Period__c ?? record.maximum_Reviews_per_Period__c
        return maxPerPeriod != null && Number(maxPerPeriod) > 0
    }

    // Derive completed from current customer / loyaltyData at render time so it stays in sync.
    const getCompletedForTile = (tileContentId) => {
        if (!customer?.isRegistered) return false
        const startShoppingNorm = (tileContentId || '').toLowerCase().replace(/-/g, '_')
        if (startShoppingNorm === 'start_shopping_tile' || startShoppingNorm === 'startshopping_tile') {
            return false
        }
        const shopBothNorm = (tileContentId || '').toLowerCase().replace(/-/g, '_')
        if (
            shopBothNorm === 'shop_online_store_tile' ||
            shopBothNorm === 'shoponlinestore_tile' ||
            tileContentId === 'shop-tile' ||
            tileContentId === 'shop_online_store-tile'
        ) {
            return isShopOnlineInStoreTileCompleted(loyaltyMemberRecord)
        }
        switch (tileContentId) {
            case 'newsletter-tile':
            case '-tile': // CMS newsletter has empty RewardTileSFID
                return !!customer?.c_email_garage_optedIn
            case 'subscribe-tile':
            case 'subsvribe_sms-tile':
                return !!customer?.c_sms_garage_optedIn
            case 'leave-review-tile':
            case 'leave_review-tile':
                return isLeaveReviewTileCompleted(loyaltyMemberRecord)
            default:
                return false
        }
    }
    rewards = rewards.map((r) => ({ ...r, completed: getCompletedForTile(r['tile-content-id']) }))

    if (enablePointsForReview) {
        if (
            reviewReward &&
            !rewards.some((reward) => reward?.['tile-content-id'] === 'leave-review-tile')
        ) {
            rewards = [...rewards, { ...reviewReward, completed: isLeaveReviewTileCompleted(loyaltyMemberRecord) }]
        }

        if (reviewTileData && !rewardsDataMapping['leave-review-tile']) {
            rewardsDataMapping = {...rewardsDataMapping, 'leave-review-tile': reviewTileData}
        }

        if (reviewPopupData && !rewardsDataMapping['leave-review-popup']) {
            rewardsDataMapping = {...rewardsDataMapping, 'leave-review-popup': reviewPopupData}
        }
    } else {
        rewards = rewards.filter((reward) => reward?.['tile-content-id'] !== 'leave-review-tile')
    }

    // Hide Subscribe to SMS tile for now (timelines)
    rewards = rewards.filter((reward) => reward?.['tile-content-id'] !== 'subscribe-tile')

    const generatePopupComponent = (popupData) => (
        <Box className="er-modal-tile">
            <Image {...styles.erModalIcon} src={popupData.icon} />
            <Text {...generateStyles(popupData?.titleStyle, {...styles.erModalTitle})}>
                {popupData.title}
            </Text>
            <Box
                className="er-modal-subtitle"
                {...generateStyles(popupData?.descriptionStyle, {...styles.erModalSubtitle})}
                dangerouslySetInnerHTML={{ __html: popupData.content }}
            />
            <Box
                className="er-modal-note"
                {...generateStyles(popupData?.additionalInfoStyle, {...styles.erModalNote})}
                dangerouslySetInnerHTML={{ __html: popupData.note }}
            />
        </Box>
    )

    const handlePopupClick = (event) => {
        const trigger = event.target.closest('[data-popup-trigger]')
        if (!trigger) return
        event.preventDefault()
        const tileWrapper = trigger.closest('[data-tile-index]')
        if (!tileWrapper) return
        const tileIndex = parseInt(tileWrapper.getAttribute('data-tile-index'))
        const reward = rewards?.[tileIndex]
        if (reward) {
            const contentId = reward['popup-content-id']
            const drawerContent = rewardsDataMapping[contentId]
            if (drawerContent) {
                if (contentId === 'newsletter-popup') {
                    setPopupType('newsletter')
                    setPopupData(drawerContent)
                    setPopupContent(null)
                } else {
                    setPopupType(null)
                    setPopupData(null)
                    setPopupContent(generatePopupComponent(drawerContent))
                }
                onOpen()
            }
        }
    }

    // Event delegation for popup triggers - must run every render (same hook count)
    useEffect(() => {
        const container = containerRef.current
        if (container && rewards?.length > 0) {
            container.addEventListener('click', handlePopupClick)
            return () => {
                container.removeEventListener('click', handlePopupClick)
            }
        }
    }, [rewards, rewardsDataMapping, onOpen])

    if (rewards?.length === 0) {
        return null
    }

    const generateTileComponent = (tileData, isCompleted, tileContentId) => {
        const titleStyles = generateStyles(tileData?.titleStyle, { ...erbStyling.erTitle })
        const isTileClickable = !isGuest && !(isCompleted && customer?.isRegistered)
        const startShoppingNorm = (tileContentId || '').toLowerCase().replace(/-/g, '_')
        const isStartShoppingTile =
            startShoppingNorm === 'start_shopping_tile' ||
            startShoppingNorm === 'startshopping_tile'
        const guestUnderline = isGuest && !isStartShoppingTile
        return (
            <Box {...erbStyling.erTile}>
                <Image {...erbStyling.erIcon} src={tileData.icon} />
                {isTileClickable ? (
                    <Link
                        {...titleStyles}
                        href="#"
                        data-popup-trigger
                    >
                        {tileData.title}
                    </Link>
                ) : (
                    <Text
                        {...titleStyles}
                        textDecoration={guestUnderline ? 'underline' : 'none'}
                        cursor="default"
                        _hover={{ textDecoration: guestUnderline ? 'underline' : 'none' }}
                    >
                        {tileData.title}
                    </Text>
                )}
                <Text {...generateStyles(tileData?.descriptionStyle, {...erbStyling.erSubtitle})}>
                    {tileData.subtitle}
                </Text>
                {isCompleted && customer?.isRegistered && (
                    <>
                        <Text {...erbStyling.erCompleted}>
                            {tileData.completedText}
                        </Text>
                        {tileData.completedCtaLabel && tileData.completedCtaHref && (
                            <Link
                                as={RouterLink}
                                to={tileData.completedCtaHref}
                                mt={2}
                                display="block"
                                fontSize="sm"
                                fontWeight="600"
                                textDecoration="underline"
                            >
                                {tileData.completedCtaLabel}
                            </Link>
                        )}
                    </>
                )}
            </Box>
        )
    }

    const handleDrawerClose = () => {
        setPopupType(null)
        setPopupData(null)
        onClose()
    }

    return (
        <Box ref={ref}>
            <Box {...epStyling.container}
                ref={containerRef}>
                <Box {...styles.componentWrapper}>
                    <Box
                        {...generateStyles(cmsData?.title?.ResponsiveTextBlockTextStyle, { ...styles.sectionTitle })}
                        {...epStyling.title}
                        sx={{
                            ...styles.highlighted,
                            ...(isGuest && { textDecoration: 'none', '&:hover': { textDecoration: 'none' } })
                        }}
                        dangerouslySetInnerHTML={{ __html: breakpointTitle }} />
                    <Box {...erbStyling.tiles} {...erbStyling.mobileGrid}>
                        {rewards?.map((reward, index) => {
                            const tileData = rewardsDataMapping[reward['tile-content-id']]
                            return (tileData &&
                                <Box
                                    key={index}
                                    sx={erbStyling.gridTileWrapper}
                                    data-tile-index={index}
                                >
                                    {generateTileComponent(tileData, reward.completed, reward['tile-content-id'])}
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
            </Box>

            <Drawer
                placement="right"
                isOpen={isOpen}
                onClose={handleDrawerClose}
                onOpen={onOpen}
                size="md"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent {...styles.drawerContent}>
                    <DrawerCloseButton />
                    <DrawerBody {...styles.drawerBody}>
                        <Box sx={styles.drawerWrapper}>
                            {popupType === 'newsletter' && popupData ? (
                                <NewsletterSubscribeDrawer
                                    popupData={popupData}
                                    styles={styles}
                                    customer={customer}
                                    onSuccess={onNewsletterSubscribed}
                                />
                            ) : (
                                popupContent
                            )}
                        </Box>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
})

EarningPoints.propTypes = {
    customer: PropTypes.object,
    loyaltyData: PropTypes.object,
    enablePointsForReview: PropTypes.bool,
    onNewsletterSubscribed: PropTypes.func
}

export default EarningPoints
